import { Injectable, computed, inject, signal } from '@angular/core';
import { ChatService } from './chat.service';
import { ChatMessage, Conversation } from '../models/chat.model';

/**
 * The customer's conversation list, shared by the inbox page and the open
 * conversation so a sent or received message updates the preview, order and
 * unread badge without refetching.
 */
@Injectable({ providedIn: 'root' })
export class ChatInboxService {
  private chatService = inject(ChatService);

  readonly conversations = signal<Conversation[]>([]);
  readonly loading = signal(false);
  readonly loadingMore = signal(false);
  readonly error = signal(false);
  readonly hasMore = signal(false);
  readonly loaded = signal(false);
  readonly unreadTotal = computed(() => this.conversations().reduce((n, c) => n + (c.unread_count || 0), 0));

  private page = 1;

  load(): void {
    if (this.loading()) return;
    this.loading.set(true);
    this.error.set(false);
    this.chatService.listConversations(1).subscribe({
      next: res => {
        this.page = 1;
        this.conversations.set(sortByActivity(res.results || []));
        this.hasMore.set(!!res.next);
        this.loading.set(false);
        this.loaded.set(true);
      },
      error: () => {
        this.loading.set(false);
        this.error.set(true);
      },
    });
  }

  loadMore(): void {
    if (this.loading() || this.loadingMore() || !this.hasMore()) return;
    this.loadingMore.set(true);
    this.chatService.listConversations(this.page + 1).subscribe({
      next: res => {
        this.page++;
        const known = new Set(this.conversations().map(c => c.id));
        this.conversations.update(list => [...list, ...(res.results || []).filter(c => !known.has(c.id))]);
        this.hasMore.set(!!res.next);
        this.loadingMore.set(false);
      },
      error: () => this.loadingMore.set(false),
    });
  }

  /**
   * Background refresh of the newest page (header badge): merges by id without
   * touching loading flags, so an open inbox doesn't flicker.
   */
  mergeLatest(latest: Conversation[]): void {
    const fresh = new Map(latest.map(c => [c.id, c]));
    const rest = this.conversations().filter(c => !fresh.has(c.id));
    this.conversations.set(sortByActivity([...latest, ...rest]));
    if (!this.loaded()) {
      this.loaded.set(true);
      this.hasMore.set(false);
    }
  }

  /** Logged out: nothing of the previous account may remain. */
  reset(): void {
    this.conversations.set([]);
    this.loaded.set(false);
    this.hasMore.set(false);
    this.error.set(false);
    this.page = 1;
  }

  /** Adds or replaces a conversation (e.g. one opened from a shop page). */
  upsert(conversation: Conversation): void {
    this.conversations.update(list => sortByActivity([conversation, ...list.filter(c => c.id !== conversation.id)]));
  }

  /** Reflects a message sent or received in the open conversation. */
  noteMessage(conversationId: string, message: ChatMessage): void {
    this.conversations.update(list => sortByActivity(list.map(c => c.id === conversationId
      ? { ...c, last_message: message, last_message_at: message.created_at }
      : c)));
  }

  markRead(conversationId: string): void {
    this.conversations.update(list => list.map(c => c.id === conversationId && c.unread_count ? { ...c, unread_count: 0 } : c));
  }
}

function sortByActivity(list: Conversation[]): Conversation[] {
  const at = (c: Conversation) => new Date(c.last_message_at || c.created_at).getTime() || 0;
  return [...list].sort((a, b) => at(b) - at(a));
}
